import StatusChip from 'components/@extended/StatusChip.tsx';
import CircularLoader from 'components/CircularLoader';
import MatterIndicators, { BOTTOM_LINE_IN_NEW_DESIGN, hasMatterAccess } from 'components/matters/MatterIndicators';
import NoData from 'components/NoData.tsx';
import { RecordLink } from 'components/record-chip/RecordLink';
import { ReactTable } from 'components/tables/BasicTable.tsx';
import type { DataTableColumnDef } from 'components/tables/data-table';
import { useIsNewDesign } from 'components/tables/list-table/filter-bar/useIsNewDesign';
import React, { useMemo } from 'react';
import { useGetMattersWithFiltersQuery } from 'store/api/matters.ts';
import { ComplianceListItem } from 'types/compliance.ts';
import { MatterListItem } from 'types/matters.ts';
import { getClassifierText, t } from 'utils/getTranslations.ts';
import { useClassifiers } from 'utils/useClassifiers.ts';
import { getStatusChipType } from '../matters/util.tsx';

// `entity-link` hands the cell's link colour and wrapping to the table's own rule.
const LINK_CLASS = 'entity-link text-fg hover:text-link w-fit font-semibold no-underline hover:underline';

const ComplianceExpandedContent: React.FC<ComplianceListItem> = ({ partyId }) => {
    const { classifiers, isLoading: isLoadingClassifiers } = useClassifiers({ codes: ['MATTER_STATE_TYPE'] });
    const isNewDesign = useIsNewDesign();
    const { data, isLoading } = useGetMattersWithFiltersQuery({
        filters: `clientParty.id==${partyId};practiceAreas.practiceArea.isAmlApplicable==true`,
        page: 0,
        pageSize: 10000,
    });

    const subRowColumns = useMemo<DataTableColumnDef<MatterListItem>[]>(
        () => [
            {
                header: t('matters.case'),
                cell: ({ row }) => {
                    const { fullName, id } = row.original;
                    return (
                        <span className="new:flex-wrap flex items-center gap-1.5">
                            <MatterIndicators
                                isConfidential={row.original.isConfidential}
                                hasAccess={hasMatterAccess(row.original)}
                                className={BOTTOM_LINE_IN_NEW_DESIGN}
                            />
                            <RecordLink className={LINK_CLASS} to={`/matter/${id}`} label={fullName ?? '-'} />
                        </span>
                    );
                },
            },
            {
                header: t('matters.aml-status'),
                cell: () => {
                    return <></>;
                },
            },
            {
                header: t('matters.aml-sent'),
                cell: () => {
                    return <></>;
                },
            },
            {
                header: t('matters.aml-received-or-na'),
                cell: () => {
                    return <></>;
                },
            },
            {
                header: t('matters.responsible-person'),
                cell: ({ row }) => {
                    const { responsibleEmployee } = row.original;
                    // No employee, no chip: the new design writes the dash classic links to nowhere.
                    return isNewDesign && !responsibleEmployee ? (
                        '-'
                    ) : (
                        <RecordLink
                            className={LINK_CLASS}
                            to={`/employee/${responsibleEmployee?.id}`}
                            label={responsibleEmployee?.name ?? '-'}
                        />
                    );
                },
            },
            {
                header: t('matters.supervising-person'),
                cell: ({ row }) => {
                    const { supervisedByEmployee } = row.original;
                    return isNewDesign && !supervisedByEmployee ? (
                        '-'
                    ) : (
                        <RecordLink
                            className={LINK_CLASS}
                            to={`/employee/${supervisedByEmployee?.id}`}
                            label={supervisedByEmployee?.name ?? '-'}
                        />
                    );
                },
            },
            {
                header: t('matters.case-status'),
                cell: ({ row }) => {
                    const { matterStateTypeCode } = row.original;
                    return (
                        <StatusChip
                            className="mt-1"
                            type={getStatusChipType(matterStateTypeCode)}
                            label={getClassifierText(classifiers['MATTER_STATE_TYPE'], matterStateTypeCode)}
                        />
                    );
                },
            },
        ],
        [classifiers, isNewDesign]
    );

    if (isLoading || isLoadingClassifiers) {
        return <CircularLoader className="static justify-start" />;
    }

    if (data?.content.length === 0) {
        return <NoData />;
    }

    return (
        <ReactTable
            data={data?.content || []}
            columns={subRowColumns}
            // The last row keeps its divider, closing the nested table off against the next parent row.
            className="bg-grey-50 [&_tr:last-child>td]:border-b"
        />
    );
};

export default ComplianceExpandedContent;
